import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import UploadWidget from '../Cloudinary/UploadWidget';
import CloudinaryImageBrowser from '../Cloudinary/CloudinaryImageBrowser';
import { Gallery, CloudinaryImage } from '../../types/Gallery';
import { categoryIsChosen, getCategoryOptions, loadCategoriesFromServer, toggleCategorySlug, uniqueCategorySlugs } from '../../utils/categories';
import './GalleryCreateModal.css';

interface GalleryCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (gallery: Gallery) => void;
}

const GalleryCreateModal: React.FC<GalleryCreateModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [newGalleryName, setNewGalleryName] = useState('');
  const [newGalleryCategories, setNewGalleryCategories] = useState<string[]>([]);
  const [newGalleryDescription, setNewGalleryDescription] = useState('');
  const [newGalleryExcerpt, setNewGalleryExcerpt] = useState('');
  const [uploadedImages, setUploadedImages] = useState<CloudinaryImage[]>([]);
  // Hero image is always the first image (simplest approach)
  const [categoryOptions, setCategoryOptions] = useState<{value: string; label: string; color: string}[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageAltTexts, setImageAltTexts] = useState<{[key: number]: string}>({});
  const [showCloudinaryBrowser, setShowCloudinaryBrowser] = useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Load dynamic categories on component mount and when modal opens
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const serverCategories = await loadCategoriesFromServer();
        const options = getCategoryOptions(serverCategories);
        setCategoryOptions(options);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategoryOptions([]);
      }
    };

    loadCategories();

    // Listen for category updates from admin panel
    const handleCategoryUpdate = () => {
      loadCategories();
    };

    // Listen for storage events (when categories are updated in another tab/component)
    window.addEventListener('storage', handleCategoryUpdate);
    
    // Listen for custom category update events
    window.addEventListener('categories_updated', handleCategoryUpdate);

    return () => {
      window.removeEventListener('storage', handleCategoryUpdate);
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);

  // Reload categories when modal opens
  useEffect(() => {
    if (isOpen) {
      const refreshCategories = async () => {
        const serverCategories = await loadCategoriesFromServer();
        setCategoryOptions(getCategoryOptions(serverCategories));
      };
      refreshCategories().catch((error) => {
        console.error('Error refreshing categories:', error);
        setCategoryOptions([]);
      });
    }
  }, [isOpen]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setNewGalleryName('');
      setNewGalleryCategories([]);
      setNewGalleryDescription('');
      setNewGalleryExcerpt('');
      setUploadedImages([]);
      setIsSubmitting(false);
      setImageAltTexts({});
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleImageUpload = (uploadedImagesArray: any[]) => {
    const newImages: CloudinaryImage[] = uploadedImagesArray.map((result: any, index: number) => ({
      publicId: result.public_id,
      public_id: result.public_id,
      url: result.secure_url,
      secure_url: result.secure_url,
      alt: result.original_filename || newGalleryName,
      filename: result.original_filename || result.public_id?.split('/').pop() || 'image',
      width: result.width || 400,
      height: result.height || 300,
    }));

    const updatedImages = [...uploadedImages, ...newImages];
    setUploadedImages(updatedImages);
    
    // Initialize alt text for new images
    const newAltTexts = {...imageAltTexts};
    newImages.forEach((_, index) => {
      const globalIndex = uploadedImages.length + index;
      newAltTexts[globalIndex] = newGalleryName || `Image ${globalIndex + 1}`;
    });
    setImageAltTexts(newAltTexts);
    
    // First image is automatically the hero image (no need to set it)
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      // For now, show a message - you could implement direct file upload here
      alert(`Dropped ${imageFiles.length} image(s). Please use the upload button to upload them to Cloudinary.`);
    }
  }, []);

  // Validation
  const chosenCategories = uniqueCategorySlugs(newGalleryCategories);
  const canCreateGallery = newGalleryName.trim().length > 0 && uploadedImages.length > 0 && chosenCategories.length > 0;

  const handleCreateGallery = async () => {
    if (!newGalleryName.trim() || uploadedImages.length === 0 || chosenCategories.length === 0) {
      alert('Please add a gallery name, choose at least one category, and upload at least one image.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update images with alt text
      const imagesWithAlt = uploadedImages.map((image, index) => ({
        ...image,
        alt: imageAltTexts[index] || image.alt || newGalleryName
      }));

      const newGallery: Gallery = {
        id: Date.now().toString(),
        name: newGalleryName.trim(),
        description: newGalleryDescription.trim(),
        excerpt: newGalleryExcerpt.trim(),
        category: chosenCategories[0],
        categories: chosenCategories,
        images: imagesWithAlt,
        heroImage: imagesWithAlt[0], // Always use first image as hero
        heroImageIndex: 0, // Always use first image as hero
        galleryType: 'mosaic', // Default gallery type
        published: true, // Default to published
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await onSave(newGallery);
      onClose();
    } catch (error) {
      console.error('Error creating gallery:', error);
      alert('Failed to create gallery. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add modal-open class to body when modal is open and scroll to top
  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      // Scroll modal content to top when it opens
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
      }
      // Also scroll window to top to ensure modal is visible
      window.scrollTo(0, 0);
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
    <div className="gallery-create-modal-overlay" onClick={onClose}>
      <div className="gallery-create-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <h2>✨ Create New Gallery</h2>
            <p>Build your perfect photo collection</p>
          </div>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <div className="form-content">
            {/* Gallery Details */}
            <div className="form-section">
              <h3>📝 Gallery Details</h3>
              <div className="form-fields">
                <div className="form-group">
                  <label htmlFor="gallery-name">Gallery Name *</label>
                  <input
                    type="text"
                    id="gallery-name"
                    value={newGalleryName}
                    onChange={(e) => setNewGalleryName(e.target.value)}
                    placeholder="Enter a descriptive name for your gallery"
                    className="modern-input"
                    required
                  />
                </div>

                <fieldset id="gallery-category" className="form-group gallery-category-choices" aria-describedby="gallery-category-hint">
                  <legend>Categories *</legend>
                  {categoryOptions.map((option) => (
                    <label key={option.value}>
                      <input
                        type="checkbox"
                        checked={categoryIsChosen(newGalleryCategories, option.value)}
                        onChange={() => setNewGalleryCategories((current) => toggleCategorySlug(current, option.value))}
                      />
                      {option.label}
                    </label>
                  ))}
                  <span id="gallery-category-hint" className="field-hint">
                    Tick every category this gallery should appear in. The first one you tick is the main category.
                  </span>
                </fieldset>

                <div className="form-group">
                  <label htmlFor="gallery-excerpt">
                    Short Excerpt
                    <span style={{ fontSize: '0.85em', color: '#6b7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                      (1-2 sentences for preview)
                    </span>
                  </label>
                  <textarea
                    id="gallery-excerpt"
                    value={newGalleryExcerpt}
                    onChange={(e) => setNewGalleryExcerpt(e.target.value)}
                    placeholder="Brief preview text for gallery cards..."
                    rows={2}
                    maxLength={200}
                    className="modern-textarea"
                  />
                  <div style={{ fontSize: '0.75em', color: '#6b7280', marginTop: '0.25rem', textAlign: 'right' }}>
                    {newGalleryExcerpt.length}/200 characters
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="gallery-description">
                    Full Description
                    <span style={{ fontSize: '0.85em', color: '#6b7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                      (optional, detailed)
                    </span>
                  </label>
                  <textarea
                    id="gallery-description"
                    value={newGalleryDescription}
                    onChange={(e) => setNewGalleryDescription(e.target.value)}
                    placeholder="Full detailed description of your gallery (optional)"
                    rows={4}
                    className="modern-textarea"
                  />
                </div>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="form-section">
              <h3>📁 Upload Images</h3>
              <div className="upload-section">
                {/* Upload Options */}
                <div className="upload-options">
                  <UploadWidget
                    onUpload={handleImageUpload}
                    buttonText={uploadedImages.length > 0 ? "📤 Upload More Images" : "📤 Upload Images"}
                    folder={newGalleryName ? `carmen_lorraine_photos/galleries/${newGalleryName.toLowerCase().replace(/\s+/g, '_')}` : 'carmen_lorraine_photos/galleries/temp'}
                    className="modern-upload-widget"
                  />
                  
                  {/* Direct Cloudinary Browser Button */}
                  <button
                    type="button"
                    className="btn btn-secondary modern-cloudinary-browser"
                    onClick={() => setShowCloudinaryBrowser(true)}
                    title="Browse current website gallery folders (carmen_lorraine_photos/galleries)"
                  >
                    📁 Browse Cloudinary
                  </button>
                </div>

                {/* Drag and Drop Zone */}
                <div 
                  className={`drag-drop-zone ${isDragOver ? 'drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="drag-drop-content">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <p>Or drag and drop images here</p>
                    <small>PNG, JPG, GIF up to 10MB each</small>
                  </div>
                </div>
                
                {uploadedImages.length > 0 && (
                  <div className="uploaded-images-preview">
                    <h4>📸 Uploaded Images ({uploadedImages.length})</h4>
                    <div className="images-grid">
                      {uploadedImages.map((image, index) => (
                        <div key={index} className="uploaded-image-wrapper">
                          <div className="uploaded-image-item">
                            <img src={image.url} alt={imageAltTexts[index] || `Uploaded ${index + 1}`} />
                            <div className="image-number">{index + 1}</div>
                          </div>
                          
                          {/* Alt Text Pill - Below Image */}
                          <div className="image-alt-pill">
                            <i className="fas fa-tag"></i>
                            <input
                              type="text"
                              value={imageAltTexts[index] || ''}
                              onChange={(e) => setImageAltTexts({...imageAltTexts, [index]: e.target.value})}
                              placeholder="Alt text for SEO..."
                              className="alt-text-input"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Hero Image Info */}
            {uploadedImages.length > 0 && (
              <div className="form-section">
                <h3>⭐ Hero Image</h3>
                <div className="hero-image-info">
                  <div className="hero-image-preview">
                    <img src={uploadedImages[0].url} alt="Hero image preview" />
                    <div className="hero-image-label">
                      <i className="fas fa-star"></i>
                      <span>First uploaded image</span>
                    </div>
                  </div>
                </div>
                <p className="hero-selection-note">
                  The first uploaded image will automatically be used as the hero image for gallery previews.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-actions">
            <button 
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>

            <button 
              className="btn btn-success" 
              onClick={handleCreateGallery}
              disabled={!canCreateGallery || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i> Create Gallery
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
    </div>
    {showCloudinaryBrowser ? createPortal(
      <div className="cloudinary-browser-overlay" onClick={() => setShowCloudinaryBrowser(false)}>
          <div className="cloudinary-browser-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cloudinary-browser-header">
              <h3>📁 Select Images from Cloudinary</h3>
              <button 
                className="close-btn"
                onClick={() => setShowCloudinaryBrowser(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="cloudinary-browser-content">
              <CloudinaryImageBrowser
                onImagesSelected={(images) => {
                  handleImageUpload(images);
                  setShowCloudinaryBrowser(false);
                }}
                onClose={() => setShowCloudinaryBrowser(false)}
                existingImages={uploadedImages.map(img => img.publicId)}
                maxSelection={20}
                insideModal={true}
              />
            </div>
          </div>
        </div>,
      document.body,
    ) : null}
    </>,
    document.body,
  );
};

export default GalleryCreateModal;